import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { SectionProps } from '../../activityComponents/models';
import { sub } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectWebskin } from 'reducers/auth';
import { selectPrivacyPolicy } from 'reducers/landingPage';
import { paths } from 'appConstants';

interface AssessmentSubmissionProps {
  handleToggle: () => void;
  isConfirmed: boolean;
  assessmentName?: string | null;
  applicantName?: string;
  style?: React.CSSProperties;
  extraClassName?: string;
  submissionPageText: string | null;
}

const getCheckboxText = (
  html: string
): { checkboxText: string | null; cleanedHtml: string | null } => {
  // Find position of marker
  const markerIndex = html.indexOf('[[AddCheckbox]]');
  if (markerIndex === -1) return { checkboxText: null, cleanedHtml: html };

  // Find enclosing <p> tag before marker
  const pStartIndex = html.lastIndexOf('<p', markerIndex);
  if (pStartIndex === -1) return { checkboxText: null, cleanedHtml: null };

  // Find closing </p> tag after marker
  const contentStart = html.indexOf('>', pStartIndex) + 1;
  const contentEnd = html.indexOf('</p>', markerIndex);

  // Extract text and remove marker
  const checkboxText = html
    .substring(contentStart, contentEnd)
    .replace('[[AddCheckbox]]', '')
    .trim();

  // Remove entire <p> tag and contents
  const cleanedHtml = html.slice(0, pStartIndex) + html.slice(contentEnd + 4);
  console.log(checkboxText, cleanedHtml);
  return { checkboxText, cleanedHtml };
};

const AssessmentSubmission: React.FC<AssessmentSubmissionProps> = ({
  handleToggle,
  isConfirmed,
  assessmentName,
  submissionPageText,
  style,
  extraClassName,
}) => {
  const webSkinDetails = useSelector(selectWebskin);
  console.log(webSkinDetails);
  const confirmationBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let checkbox: HTMLInputElement | null | undefined = null;
    if (confirmationBoxRef.current) {
      const checkboxElement = confirmationBoxRef.current?.querySelector(
        'input[type="checkbox"]'
      );
      if (checkboxElement) {
        checkbox = checkboxElement as HTMLInputElement;
        checkbox.checked = isConfirmed;
        checkbox.addEventListener('change', (e) => {
          console.log('Checkbox clicked', e);
          handleToggle();
        });
      }
      // In preparation for accessibility improvements deployment
      // const elements = confirmationBoxRef.current?.querySelectorAll(
      //   'p, h1, ul, li, a, b'
      // );
      // if (style && elements) {
      //   elements.forEach((element) => {
      //     if (element instanceof HTMLElement) {
      //       for (const [key, value] of Object.entries(style)) {
      //         element.style[key as any] = value;
      //       }
      //     }
      //   });

      return () => {
        if (checkbox) {
          checkbox.removeEventListener('change', handleToggle);
        }
      };
    }
  }, [handleToggle]);

  if (submissionPageText) {
    const { checkboxText, cleanedHtml } = getCheckboxText(submissionPageText);
    return checkboxText && cleanedHtml ? (
      <div className="AssessmentSubmission" style={style}>
        <div style={style} dangerouslySetInnerHTML={{ __html: cleanedHtml }} />
        {/* // Current master version */}
        {/* <div className="AssessmentConfirmationBox">
          <span dangerouslySetInnerHTML={{ __html: checkboxText }} />
          <div className="CheckboxFieldContainer">
            <label className="Label">
              <input
                checked={isConfirmed}
                type="checkbox"
                className="CheckboxInput"
                name={'checkbox'}
                onChange={handleToggle}
              />
              <span className="Checkmark"></span>
            </label>
          </div>
        </div> */}
        <div className="CheckboxBlock">
          <input
            name="checkbox"
            id="checkbox"
            type="checkbox"
            checked={isConfirmed}
            onChange={handleToggle}
          />
          <label
            htmlFor="checkbox"
            className="CustomInput"
            dangerouslySetInnerHTML={{ __html: checkboxText }}
          />
        </div>
      </div>
    ) : cleanedHtml ? (
      <div
        className="AssessmentSubmission"
        ref={confirmationBoxRef}
        style={style}
        dangerouslySetInnerHTML={{ __html: cleanedHtml }}
      />
    ) : (
      SubmissionFallback(style, assessmentName, isConfirmed, handleToggle)
    );
  }

  return SubmissionFallback(style, assessmentName, isConfirmed, handleToggle);
};

export { AssessmentSubmission };

const SubmissionFallback = (
  style: React.CSSProperties | undefined,
  assessmentName: string | null | undefined,
  isConfirmed: boolean,
  handleToggle: () => void
) => {
  const privacyPolicy = useSelector(selectPrivacyPolicy);

  return (
    <div className="AssessmentSubmission" style={style}>
      <h1>Nearly done</h1>
      <p>Thanks for taking the time to complete this assessment.</p>
      <p>
        Before you finish you just need to take a look at the points below and
        let us know that you’re happy with them:
      </p>
      <ul style={style}>
        <li style={style}>
          I’m happy for my responses to be securely stored and used as part of
          the {assessmentName ? `${assessmentName} ` : ''}
          assessment process
        </li>
        <li style={style}>
          I declare that the information I have provided is an honest and
          complete picture
        </li>
      </ul>
      <p className="" style={style}>
        <b style={style}>
          You can find everything you need to know about how we collect, process
          and store your data in our{' '}
          <a
            href={`${privacyPolicy ? paths.privacyPolicy.url : "https://www.clevry.com/en/privacy-policy/"}`}
            target="_blank"
            rel="noopener noreferrer"
            style={style}
          >
            Privacy Policy
          </a>
          . If you’re happy with this and ready to send us your assessment
          responses, please check the box and click submit responses.
        </b>
      </p>
      <div className="CheckboxBlock">
        <input name="checkbox" id="checkbox" type="checkbox" checked={isConfirmed} onChange={handleToggle} />
        <label htmlFor="checkbox" className="CustomInput"><b>I confirm that I have read and understand these points</b></label>
      </div>
    </div>
  );
};
